import { getDefaultFormValues } from "shared/Scan/helpers";
import { ScanFormFields } from "shared/Scan/types";

import { ScanSettingsItem } from "../gql";

export type ScanSettingsDetailsFormFields = Pick<
  ScanFormFields,
  "name" | "space" | "labels" | "description"
>;

export const getFormDefaultValues = (
  scanSettings: ScanSettingsItem
): ScanSettingsDetailsFormFields => {
  const { name, space, labels, description } = getDefaultFormValues(scanSettings);
  return { name, space, labels, description };
};

type SpaceCompatibilityFormOptions = {
  integrations: { id: string }[];
  workerPools: { id: string }[];
  loading: boolean;
  error: unknown;
};

type IncompatibleSpaceCheck = {
  hasIncompatibleIntegration: boolean;
  hasIncompatibleWorkerPool: boolean;
};

// Scan edits submit the full scan config (not a partial patch), so a space picked
// here must still be able to reach the integration/worker pool set on the other tabs —
// otherwise saving fails server-side with an error about fields the user never touched.
export const getIncompatibleSpaceCheck = (
  scanSettings: ScanSettingsItem,
  isSpaceChanged: boolean,
  formOptions: SpaceCompatibilityFormOptions
): IncompatibleSpaceCheck => {
  const canCheck = isSpaceChanged && !formOptions.loading && !formOptions.error;
  const currentIntegrationId = scanSettings.providerConfigs[0]?.integration;
  const currentWorkerPoolId = scanSettings.workerPool;

  return {
    hasIncompatibleIntegration:
      canCheck &&
      !!currentIntegrationId &&
      !formOptions.integrations.some((integration) => integration.id === currentIntegrationId),
    hasIncompatibleWorkerPool:
      canCheck &&
      !!currentWorkerPoolId &&
      !formOptions.workerPools.some((workerPool) => workerPool.id === currentWorkerPoolId),
  };
};

export const getIncompatibleSpaceMessage = ({
  hasIncompatibleIntegration,
  hasIncompatibleWorkerPool,
}: IncompatibleSpaceCheck): string => {
  if (hasIncompatibleIntegration && hasIncompatibleWorkerPool) {
    return "This space doesn't have access to the scan's current cloud integration or worker pool. Update Cloud provider and Worker pool settings first, or choose a different space.";
  }

  if (hasIncompatibleIntegration) {
    return "This space doesn't have access to the scan's current cloud integration. Update Cloud provider settings first, or choose a different space.";
  }

  return "This space doesn't have access to the scan's current worker pool. Update Worker pool settings first, or choose a different space.";
};
